import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Computer as ComputerIcon,
  MoreVert,
  Refresh as RefreshIcon,
  ViewAgenda,
  ViewList,
  WarningAmber,
} from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const VIEW_KEY = 'teks-monitoring-computers-view';

function operationalChipColor(status: string): 'success' | 'warning' | 'error' | 'default' | 'info' {
  switch (String(status || '').toUpperCase()) {
    case 'ONLINE':
      return 'success';
    case 'IDLE':
    case 'LOCKED':
    case 'ATTENTION':
      return 'warning';
    case 'EMERGENCY ACCESS':
      return 'error';
    case 'OFFLINE':
    default:
      return 'default';
  }
}

function healthChipColor(health: string): 'success' | 'warning' | 'error' | 'default' {
  switch (String(health || '').toUpperCase()) {
    case 'HEALTHY':
      return 'success';
    case 'WARNING':
      return 'warning';
    case 'CRITICAL':
    case 'OFFLINE':
      return 'error';
    default:
      return 'default';
  }
}

const ComputersPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canViewLiveScreen = (user?.permissions || []).includes('monitoring.live_screen.view');
  const canConfigureLiveScreen = (user?.permissions || []).includes('monitoring.live_screen.configure');
  const canStartRemoteControl = (user?.permissions || []).includes('monitoring.remote_control.start');
  const canReadRemoteControl = (user?.permissions || []).includes('monitoring.remote_control.read')
    || canStartRemoteControl;
  const isSuperUser = user?.role === 'super_user';
  const [searchTerm, setSearchTerm] = useState('');
  const [uninstallOpen, setUninstallOpen] = useState(false);
  const [uninstallDevice, setUninstallDevice] = useState<any | null>(null);
  const [uninstallConfirmName, setUninstallConfirmName] = useState('');
  const [uninstallBusy, setUninstallBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [healthFilter, setHealthFilter] = useState('');
  const [activationTab, setActivationTab] = useState<'activated' | 'not_activated'>('activated');
  const [viewMode, setViewMode] = useState<'list' | 'cards'>(() => {
    const saved = localStorage.getItem(VIEW_KEY);
    return saved === 'cards' ? 'cards' : 'list';
  });
  const [editOpen, setEditOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({ deviceName: '', notes: '', userId: '', isActive: true });
  const [error, setError] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuDevice, setMenuDevice] = useState<any | null>(null);

  const queryParams = useMemo(() => ({
    search: searchTerm || undefined,
    status: statusFilter || undefined,
    employee_id: employeeFilter || undefined,
    campaign_id: campaignFilter || undefined,
    agent_health: healthFilter || undefined,
    activation: activationTab,
  }), [searchTerm, statusFilter, employeeFilter, campaignFilter, healthFilter, activationTab]);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['monitoringDevicesInventory', queryParams],
    queryFn: () => apiClient.monitoring.listDevices(queryParams),
    refetchInterval: 30000,
  });

  const { data: usersData } = useQuery({
    queryKey: ['monitoringFilterUsers'],
    queryFn: () => apiClient.users.getAll(),
    staleTime: 60_000,
  });

  const { data: campaignsData } = useQuery({
    queryKey: ['monitoringFilterCampaigns'],
    queryFn: () => apiClient.campaigns.getAll(),
    staleTime: 60_000,
  });

  const devices = data?.data?.data || [];
  const counts = data?.data?.counts || {
    total: devices.length,
    online: devices.filter((d: any) => d.online).length,
    offline: devices.filter((d: any) => !d.online).length,
    attention: devices.filter((d: any) => d.needs_attention).length,
    activated: 0,
    not_activated: 0,
  };
  const showingNotActivated = activationTab === 'not_activated';
  const users = usersData?.data?.data || [];
  const campaigns = campaignsData?.data?.data || [];

  const setView = (_: React.MouseEvent<HTMLElement>, next: 'list' | 'cards' | null) => {
    if (!next) return;
    setViewMode(next);
    localStorage.setItem(VIEW_KEY, next);
  };

  const openComputer = (device: any) => {
    navigate(`/workforce-monitoring/computers/${device.id}`);
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, device: any) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuDevice(device);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuDevice(null);
  };

  const openEdit = (device: any) => {
    setSelectedDevice(device);
    setEditForm({
      deviceName: device.device_name || '',
      notes: device.notes || '',
      userId: device.employee?.id || '',
      isActive: true,
    });
    setEditOpen(true);
    closeMenu();
  };

  const saveDevice = async () => {
    if (!selectedDevice) return;
    try {
      await apiClient.devices.update(selectedDevice.id, {
        deviceName: editForm.deviceName,
        notes: editForm.notes,
        // Empty string must become null so PUT clears the assignment (undefined omits the field).
        userId: editForm.userId || null,
        isActive: editForm.isActive,
      });
      setEditOpen(false);
      queryClient.invalidateQueries({ queryKey: ['monitoringDevicesInventory'] });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update device.');
    }
  };

  const toggleTracking = async (device: any) => {
    const currentlyOn = device.is_tracked !== false;
    await apiClient.devices.updateTracking(device.id, !currentlyOn);
    queryClient.invalidateQueries({ queryKey: ['monitoringDevicesInventory'] });
    closeMenu();
  };

  const deleteDevice = async (device: any) => {
    if (showingNotActivated) {
      if (!window.confirm(
        `Permanently delete "${device.device_name}" from TEKS TIME?\n\nThis removes the computer from inventory and cannot be undone. It does not uninstall the Agent on the PC.`,
      )) return;
      await apiClient.devices.delete(device.id, { permanent: true });
    } else {
      if (!window.confirm(
        `Deactivate "${device.device_name}"?\n\nIt will move to No active devices. You can permanently delete it from that tab if you no longer need it.`,
      )) return;
      await apiClient.devices.delete(device.id);
    }
    queryClient.invalidateQueries({ queryKey: ['monitoringDevicesInventory'] });
    closeMenu();
  };

  const reactivateDevice = async (device: any) => {
    await apiClient.devices.update(device.id, { isActive: true });
    queryClient.invalidateQueries({ queryKey: ['monitoringDevicesInventory'] });
    closeMenu();
  };

  const emptyMessage = Object.values(queryParams).some((v) => v && v !== 'activated')
    ? showingNotActivated
      ? 'No inactive computers match the selected filters.'
      : 'No computers match the selected filters.'
    : showingNotActivated
      ? 'No inactive computers. Soft-deactivated devices appear here until permanently deleted.'
      : 'No TEKS TIME computers have registered yet. Computers appear automatically after the TEKS TIME Agent is installed.';

  const renderVersion = (device: any) => {
    const version = device.agent_version ? `v${String(device.agent_version).replace(/^v/i, '')}` : '—';
    if (device.version_status === 'outdated') return `${version} · UPDATE AVAILABLE`;
    if (device.version_status === 'current' || device.version_status === 'newer') return `${version} · CURRENT`;
    return version;
  };

  const listView = (
    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Computer</TableCell>
            <TableCell>Employee</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Work State</TableCell>
            <TableCell>Agent Health</TableCell>
            <TableCell>Current Activity</TableCell>
            <TableCell>Last Heartbeat</TableCell>
            <TableCell>Alerts</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow><TableCell colSpan={9}><CircularProgress size={20} /></TableCell></TableRow>
          ) : devices.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{emptyMessage}</Typography>
              </TableCell>
            </TableRow>
          ) : devices.map((device: any) => (
            <TableRow
              key={device.id}
              hover
              selected={Boolean(device.needs_attention)}
              sx={{ cursor: 'pointer' }}
              onClick={() => openComputer(device)}
            >
              <TableCell>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <ComputerIcon fontSize="small" sx={{ mt: 0.3 }} />
                  <Box>
                    <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                      <Typography variant="body2" fontWeight={600}>{device.device_name}</Typography>
                      {device.pending_agent_uninstall && (
                        <Chip size="small" color="warning" label="Uninstall pending" sx={{ ml: 1, height: 20 }} />
                      )}
                      {device.pending_agent_uninstall_started && (
                        <Chip size="small" color="error" label="Uninstall started" sx={{ ml: 1, height: 20 }} />
                      )}
                      {device.live_screen_status_label && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={device.live_screen_status === 'AVAILABLE' ? 'success' : 'default'}
                          label={`Live Screen · ${device.live_screen_status_label}`}
                          sx={{ height: 20 }}
                        />
                      )}
                      {!device.live_screen_status_label && canViewLiveScreen && device.online && (
                        <Chip size="small" variant="outlined" label="Live Screen" sx={{ height: 20 }} />
                      )}
                      {device.call_monitoring_status_label && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={device.call_monitoring_effective ? 'success' : 'default'}
                          label={`Call Monitoring · ${device.call_monitoring_status_label}`}
                          sx={{ height: 20 }}
                        />
                      )}
                      {canReadRemoteControl && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={
                            device.remote_control_global_enabled && device.remote_control_device_enabled
                              ? 'success'
                              : 'default'
                          }
                          label={`Remote Control · ${
                            device.remote_control_status_label
                            || (device.remote_control_global_enabled === false
                              ? 'Disabled globally'
                              : device.remote_control_device_enabled
                                ? 'Available'
                                : 'Disabled')
                          }`}
                          sx={{ height: 20 }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {[device.os_version, device.windows_user].filter(Boolean).join(' · ') || 'Windows'}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{device.employee?.name || 'Unassigned'}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {device.campaign || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip size="small" color={operationalChipColor(device.operational_status)} label={device.operational_status || (device.online ? 'ONLINE' : 'OFFLINE')} />
              </TableCell>
              <TableCell>{device.work_state_label || '—'}</TableCell>
              <TableCell>
                <Typography variant="body2">{device.agent_health || '—'}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {renderVersion(device)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>{device.current_activity || '—'}</Typography>
              </TableCell>
              <TableCell>{device.last_heartbeat_label || 'Never'}</TableCell>
              <TableCell>
                {device.alert_count > 0 ? (
                  <Tooltip
                    arrow
                    title={(
                      <Box sx={{ py: 0.5 }}>
                        {(device.alerts || []).map((alert: any, idx: number) => (
                          <Typography
                            key={idx}
                            variant="caption"
                            component="div"
                            sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, lineHeight: 1.5 }}
                          >
                            <span>{String(alert.severity).toUpperCase() === 'CRITICAL' ? '●' : '▲'}</span>
                            <span>{alert.message}</span>
                          </Typography>
                        ))}
                        <Typography variant="caption" component="div" sx={{ mt: 0.5, opacity: 0.7 }}>
                          Click to view details
                        </Typography>
                      </Box>
                    )}
                  >
                    <Chip
                      size="small"
                      color="warning"
                      icon={<WarningAmber />}
                      label={`${device.alert_count} Warning${device.alert_count === 1 ? '' : 's'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/workforce-monitoring/computers/${device.id}?tab=diagnostics`);
                      }}
                    />
                  </Tooltip>
                ) : '—'}
              </TableCell>
              <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                {showingNotActivated ? (
                  <>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => deleteDevice(device)}
                    >
                      Delete
                    </Button>
                    <Button size="small" sx={{ ml: 0.5 }} onClick={() => reactivateDevice(device)}>
                      Reactivate
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="small" variant="contained" onClick={() => openComputer(device)}>Open</Button>
                    <IconButton size="small" onClick={(e) => openMenu(e, device)} aria-label="More actions">
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const cardsView = (
    <Grid container spacing={2}>
      {isLoading ? (
        <Grid item xs={12}><CircularProgress size={24} /></Grid>
      ) : devices.length === 0 ? (
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography color="text.secondary">{emptyMessage}</Typography>
          </Paper>
        </Grid>
      ) : devices.map((device: any) => (
        <Grid item xs={12} sm={6} lg={4} key={device.id}>
          <Card
            variant="outlined"
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderColor: device.needs_attention ? 'warning.main' : undefined,
              cursor: 'pointer',
            }}
            onClick={() => openComputer(device)}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1.5}>
                <Box display="flex" gap={1} alignItems="center">
                  <ComputerIcon color="action" />
                  <Typography variant="subtitle1" fontWeight={700}>{device.device_name}</Typography>
                  {device.pending_agent_uninstall && (
                    <Chip size="small" color="warning" label="Uninstall pending" sx={{ height: 20 }} />
                  )}
                </Box>
                <Chip size="small" color={operationalChipColor(device.operational_status)} label={device.operational_status || 'OFFLINE'} />
              </Box>

              <Typography variant="body2" fontWeight={600}>{device.employee?.name || 'Unassigned'}</Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                {device.campaign || 'No campaign'}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 0.5, columnGap: 1, mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Work State</Typography>
                <Typography variant="caption">{device.work_state_label || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">Agent</Typography>
                <Typography variant="caption">
                  <Chip size="small" sx={{ height: 20 }} color={healthChipColor(device.agent_health)} label={device.agent_health || '—'} />
                  {' '}{renderVersion(device)}
                </Typography>
                <Typography variant="caption" color="text.secondary">Service</Typography>
                <Typography variant="caption">{device.service_status}</Typography>
                <Typography variant="caption" color="text.secondary">Companion</Typography>
                <Typography variant="caption">{device.companion_status}</Typography>
                <Typography variant="caption" color="text.secondary">Browser Ext.</Typography>
                <Typography variant="caption" color={device.browser_extension_connected ? 'text.primary' : 'warning.main'}>
                  {device.browser_extension_status}
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary" display="block">Current Activity</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>{device.current_activity || '—'}</Typography>

              <Typography variant="caption" color="text.secondary">
                Last heartbeat · {device.last_heartbeat_label || 'Never'}
              </Typography>

              {canReadRemoteControl && (
                <Box mt={1}>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={
                      device.remote_control_global_enabled && device.remote_control_device_enabled
                        ? 'success'
                        : 'default'
                    }
                    label={`Remote Control · ${
                      device.remote_control_status_label
                      || (device.remote_control_device_enabled ? 'Available' : 'Disabled')
                    }`}
                    sx={{ height: 20 }}
                  />
                </Box>
              )}

              {device.alerts?.length > 0 && (
                <Tooltip
                  arrow
                  title={(
                    <Box sx={{ py: 0.5 }}>
                      {device.alerts.map((alert: any, idx: number) => (
                        <Typography
                          key={idx}
                          variant="caption"
                          component="div"
                          sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, lineHeight: 1.5 }}
                        >
                          <span>{String(alert.severity).toUpperCase() === 'CRITICAL' ? '●' : '▲'}</span>
                          <span>{alert.message}</span>
                        </Typography>
                      ))}
                      <Typography variant="caption" component="div" sx={{ mt: 0.5, opacity: 0.7 }}>
                        Click to view details
                      </Typography>
                    </Box>
                  )}
                >
                  <Alert
                    severity="warning"
                    sx={{ mt: 1.5, py: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/workforce-monitoring/computers/${device.id}?tab=diagnostics`);
                    }}
                  >
                    {device.alerts[0].message}
                    {device.alerts.length > 1 ? ` (+${device.alerts.length - 1})` : ''}
                  </Alert>
                </Tooltip>
              )}
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }} onClick={(e) => e.stopPropagation()}>
              {showingNotActivated ? (
                <>
                  <Button size="small" onClick={() => reactivateDevice(device)}>Reactivate</Button>
                  <Button size="small" color="error" variant="outlined" onClick={() => deleteDevice(device)}>
                    Delete
                  </Button>
                </>
              ) : (
                <>
                  <Button size="small" variant="contained" onClick={() => openComputer(device)}>
                    Open Computer
                  </Button>
                  <IconButton size="small" onClick={(e) => openMenu(e, device)} aria-label="More actions">
                    <MoreVert fontSize="small" />
                  </IconButton>
                </>
              )}
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h6">Computers</Typography>
          <Typography variant="body2" color="text.secondary">
            {counts.online ?? 0} online · {counts.total ?? devices.length} shown
            {counts.attention ? ` · ${counts.attention} attention` : ''}
            {counts.offline ? ` · ${counts.offline} offline` : ''}
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <ToggleButtonGroup size="small" exclusive value={viewMode} onChange={setView}>
            <ToggleButton value="list" aria-label="List view"><ViewList fontSize="small" />&nbsp;List</ToggleButton>
            <ToggleButton value="cards" aria-label="Cards view"><ViewAgenda fontSize="small" />&nbsp;Cards</ToggleButton>
          </ToggleButtonGroup>
          <Button size="small" startIcon={<RefreshIcon />} variant="outlined" onClick={() => refetch()} disabled={isFetching}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Tabs
        value={activationTab}
        onChange={(_, next: 'activated' | 'not_activated') => setActivationTab(next)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          value="activated"
          label={`Active devices (${counts.activated ?? 0})`}
        />
        <Tab
          value="not_activated"
          label={`No active devices (${counts.not_activated ?? 0})`}
        />
      </Tabs>

      {showingNotActivated && (
        <Alert severity="info" sx={{ mb: 2 }}>
          These computers are not active in inventory. Use <strong>Delete</strong> to remove them permanently, or <strong>Reactivate</strong> to restore them.
        </Alert>
      )}

      <Box display="flex" gap={1.5} mb={2} flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          label="Search computers"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 220 }}
          placeholder="Name, employee, device ID…"
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(String(e.target.value))}>
            <MenuItem value="">All</MenuItem>
            {['ONLINE', 'OFFLINE', 'IDLE', 'LOCKED', 'ATTENTION', 'EMERGENCY ACCESS'].map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Employee</InputLabel>
          <Select label="Employee" value={employeeFilter} onChange={(e) => setEmployeeFilter(String(e.target.value))}>
            <MenuItem value="">All</MenuItem>
            {users.map((user: any) => (
              <MenuItem key={user.id} value={user.id}>
                {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Campaign</InputLabel>
          <Select label="Campaign" value={campaignFilter} onChange={(e) => setCampaignFilter(String(e.target.value))}>
            <MenuItem value="">All</MenuItem>
            {campaigns.map((campaign: any) => (
              <MenuItem key={campaign.id} value={campaign.id}>{campaign.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Agent Health</InputLabel>
          <Select label="Agent Health" value={healthFilter} onChange={(e) => setHealthFilter(String(e.target.value))}>
            <MenuItem value="">All</MenuItem>
            {['HEALTHY', 'WARNING', 'CRITICAL', 'OFFLINE'].map((h) => (
              <MenuItem key={h} value={h}>{h}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {viewMode === 'list' ? listView : cardsView}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => { if (menuDevice) navigate(`/workforce-monitoring/computers/${menuDevice.id}?tab=diagnostics`); closeMenu(); }}>
          View Diagnostics
        </MenuItem>
        {canViewLiveScreen && (
          <MenuItem onClick={() => { if (menuDevice) navigate(`/workforce-monitoring/computers/${menuDevice.id}?tab=live-screen`); closeMenu(); }}>
            Live Screen
          </MenuItem>
        )}
        {canConfigureLiveScreen && menuDevice && (
          <MenuItem
            onClick={async () => {
              try {
                const next = menuDevice.live_screen_device_enabled === false;
                await apiClient.monitoring.updateDeviceLiveScreen(menuDevice.id, { enabled: next });
                queryClient.invalidateQueries({ queryKey: ['monitoringDevicesInventory'] });
              } catch (err: any) {
                setError(err?.response?.data?.message || 'Failed to update Live Screen setting');
              }
              closeMenu();
            }}
          >
            {menuDevice.live_screen_device_enabled === false ? 'Enable Live Screen' : 'Disable Live Screen'}
          </MenuItem>
        )}
        {canStartRemoteControl && menuDevice && (
          <MenuItem
            onClick={async () => {
              try {
                const next = menuDevice.remote_control_device_enabled !== true;
                await apiClient.monitoring.updateDeviceRemoteControl(menuDevice.id, { enabled: next });
                queryClient.invalidateQueries({ queryKey: ['monitoringDevicesInventory'] });
              } catch (err: any) {
                setError(err?.response?.data?.message || 'Failed to update Remote Control setting');
              }
              closeMenu();
            }}
          >
            {menuDevice.remote_control_device_enabled === true ? 'Disable Remote Control' : 'Enable Remote Control'}
          </MenuItem>
        )}
        <MenuItem onClick={() => { if (menuDevice) navigate(`/workforce-monitoring/computers/${menuDevice.id}?tab=activity`); closeMenu(); }}>
          View Activity
        </MenuItem>
        <MenuItem onClick={() => { if (menuDevice) navigate(`/workforce-monitoring/computers/${menuDevice.id}?tab=applications`); closeMenu(); }}>
          View Applications
        </MenuItem>
        <MenuItem onClick={() => { if (menuDevice) navigate(`/workforce-monitoring/computers/${menuDevice.id}?tab=web`); closeMenu(); }}>
          View Web Activity
        </MenuItem>
        <MenuItem onClick={() => { if (menuDevice) navigate(`/workforce-monitoring/computers/${menuDevice.id}?tab=sessions`); closeMenu(); }}>
          View Sessions
        </MenuItem>
        <MenuItem onClick={() => menuDevice && openEdit(menuDevice)}>Rename / Assign</MenuItem>
        <MenuItem onClick={() => menuDevice && toggleTracking(menuDevice)}>
          {menuDevice?.is_tracked !== false ? 'Turn off activity capture' : 'Turn on activity capture'}
        </MenuItem>
        {showingNotActivated ? (
          <>
            <MenuItem onClick={() => menuDevice && reactivateDevice(menuDevice)}>Reactivate Device</MenuItem>
            <MenuItem onClick={() => menuDevice && deleteDevice(menuDevice)} sx={{ color: 'error.main' }}>
              Delete Permanently…
            </MenuItem>
          </>
        ) : (
          <MenuItem onClick={() => menuDevice && deleteDevice(menuDevice)} sx={{ color: 'error.main' }}>
            Deactivate Device…
          </MenuItem>
        )}
        {isSuperUser && menuDevice && !menuDevice.pending_agent_uninstall_started && (
          menuDevice.pending_agent_uninstall ? (
            <MenuItem
              onClick={async () => {
                if (!menuDevice) return;
                closeMenu();
                try {
                  await apiClient.monitoring.cancelAgentUninstall(menuDevice.id);
                  queryClient.invalidateQueries({ queryKey: ['monitoringDevicesInventory'] });
                } catch (err: any) {
                  setError(err?.response?.data?.message || 'Failed to cancel Agent uninstall');
                }
              }}
              sx={{ color: 'warning.main' }}
            >
              Cancel pending Agent uninstall…
            </MenuItem>
          ) : (
            <MenuItem
              onClick={() => {
                if (!menuDevice) return;
                setUninstallDevice(menuDevice);
                setUninstallConfirmName('');
                setUninstallOpen(true);
                closeMenu();
              }}
              sx={{ color: 'error.main' }}
            >
              Uninstall Agent remotely…
            </MenuItem>
          )
        )}
      </Menu>

      <Dialog open={uninstallOpen} onClose={() => !uninstallBusy && setUninstallOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Uninstall Agent remotely</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This queues a silent uninstall on the computer. The Agent Windows Service removes itself
            on the next heartbeat (usually within about a minute) if the PC is online. Super User only.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Computer: <strong>{uninstallDevice?.device_name || uninstallDevice?.computer_name || '—'}</strong>
          </Typography>
          <TextField
            fullWidth
            margin="dense"
            label="Type the computer name to confirm"
            value={uninstallConfirmName}
            onChange={(e) => setUninstallConfirmName(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={uninstallBusy} onClick={() => setUninstallOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={
              uninstallBusy
              || !uninstallDevice
              || uninstallConfirmName.trim().toLowerCase()
                !== String(uninstallDevice?.device_name || '').trim().toLowerCase()
            }
            onClick={async () => {
              if (!uninstallDevice) return;
              setUninstallBusy(true);
              setError(null);
              try {
                await apiClient.monitoring.requestAgentUninstall(uninstallDevice.id, {
                  confirm_device_name: uninstallConfirmName.trim(),
                });
                setUninstallOpen(false);
                queryClient.invalidateQueries({ queryKey: ['monitoringDevicesInventory'] });
              } catch (err: any) {
                setError(err?.response?.data?.message || 'Failed to queue Agent uninstall');
              } finally {
                setUninstallBusy(false);
              }
            }}
          >
            {uninstallBusy ? 'Queuing…' : 'Uninstall Agent'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Computer</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="dense" label="Label / Device Name" value={editForm.deviceName} onChange={(e) => setEditForm({ ...editForm, deviceName: e.target.value })} />
          <Autocomplete
            options={users}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
            value={users.find((u: any) => u.id === editForm.userId) || null}
            onChange={(_, value) => setEditForm({ ...editForm, userId: value?.id || '' })}
            renderInput={(params) => <TextField {...params} margin="dense" label="Assigned Employee" />}
          />
          <TextField fullWidth margin="dense" label="Notes" multiline minRows={2} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
          <FormControlLabel control={<Switch checked={editForm.isActive} onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveDevice}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ComputersPage;
